// composables/useCart.js - REFACTORED VERSION
import { useAxiosRepo } from '@pinia-orm/axios';
import { useAsyncState } from '@vueuse/core';
import { useRepo } from 'pinia-orm';
import { computed, nextTick, ref, watch } from 'vue';
import { useAccountGate } from '@/composables/useAccountGate';
import { OrderBoxStatus } from '@/enums/commerce/OrderBoxStatus.enum.js';
import Bookmark from '@/models/Bookmark.js';
import OrderBox from '@/models/commerce/OrderBox.js';
import OrderBoxItem from '@/models/commerce/OrderBoxItem.js';
import Post from '@/models/posts/Post.js';
import { patchHeldPost } from '@/models/posts/postFeedModels.js';
import TadbeesaPost from '@/models/posts/TadbeesaPost.js';
import OrderBoxRepository from '@/repositories/OrderBoxRepository.js';


const fetchedBoxIds = ref(new Set());
const fetchedPostIds = ref(new Set());
/**
 * Cart Composable - Refactored for Production
 *
 * CRITICAL FIX: All async functions now properly accept delay as first parameter
 * to match useAsyncState execute() signature where first argument is delay
 *
 * Usage: execute(delay, ...actualParams)
 * Example: await updateItem(0, boxId, itemId, updates)
 */
export function useCart() {
  // ===========================================================================
  // REPOSITORIES
  // ===========================================================================
  const orderBoxRepo = computed(() => useRepo(OrderBox));
  const orderBoxItemRepo = computed(() => useRepo(OrderBoxItem));
  const { guard } = useAccountGate();

  // ===========================================================================
  // STATE
  // ===========================================================================
  const selectedBoxId = ref(null);


  // ===========================================================================
  // COMPUTED - Box Collections
  // ===========================================================================
  const orderBoxes = computed(() => orderBoxRepo.value.get());

  const activeBoxes = computed(() =>
    orderBoxRepo.value
      .where('status', (status) =>
        [
          OrderBoxStatus.DRAFT,
          OrderBoxStatus.ACTIVE,
          OrderBoxStatus.READY,
          OrderBoxStatus.PACKED,
          OrderBoxStatus.SHIPPED,
        ].includes(status),
      )
      .get(),
  );
  // The pick list for a NEW order. A carton she said she is done ordering into
  // («خلصت طلباتي», `doneOrderingAt`) is left out: the server refuses new demand
  // into it (409 `order_box.done_ordering`), so offering it would hand her a button
  // that cannot work. `activeBoxes` keeps listing it — it is still her carton.
  // When this leaves her none, the existing "open a new carton" path takes over.
  const availableBoxes = computed(() =>
    orderBoxRepo.value
      .where('status', (status) =>
        [
          OrderBoxStatus.DRAFT,
          OrderBoxStatus.ACTIVE,
        ].includes(status),
      )
      .get()
      .filter((box) => !box.doneOrderingAt),
  );

  const closedBoxes = computed(() =>
    orderBoxRepo.value
      .where('status', (status) =>
        [
          OrderBoxStatus.DELIVERED,
          OrderBoxStatus.CANCELLED,
          OrderBoxStatus.CLOSED,
        ].includes(status),
      )
      .get(),
  );

  const selectedBox = computed(() =>
    selectedBoxId.value
      ? orderBoxRepo.value.with('items', (query) => {
        query.with('product');
      }).find(selectedBoxId.value)
      : null,
  );

  // ===========================================================================
  // CREATE - New Order Box
  // FIXED: Accept delay as first parameter
  // ===========================================================================
  const {
    isLoading: isCreatingBox,
    error: createBoxError,
    execute: createBox,
  } = useAsyncState(
    async (boxData) => {
      if (!guard('createOrderBox')) return null;
      const newBox = await OrderBoxRepository.create(boxData);
      selectedBoxId.value = newBox.id;
      return newBox;
    },
    null,
    { immediate: false, resetOnExecute: false },
  );

  // ===========================================================================
  // READ - Fetch All Boxes
  // FIXED: Accept delay as first parameter
  // ===========================================================================
  const {
    isLoading: isFetchingAll,
    error: fetchAllError,
    execute: fetchAllBoxes,
  } = useAsyncState(
    async (force = false) => {
      return await OrderBoxRepository.fetchAll(force);
    },
    null,
    { immediate: false, resetOnExecute: false },
  );

  // ===========================================================================
  // READ - Fetch Single Box (with caching)
  // FIXED: Accept delay as first parameter
  // ===========================================================================
  const {
    isLoading: isFetchingBox,
    error: fetchBoxError,
    execute: fetchBox,
  } = useAsyncState(
    async (boxId, force = false) => {
      if (!force && fetchedBoxIds.value.has(boxId)) {
        return orderBoxRepo.value.with('items', (query) => {
          query.with('product');
        }).find(boxId);
      }

      const box = await OrderBoxRepository.fetchBoxItems(boxId);
      fetchedBoxIds.value.add(boxId);
      return box;
    },
    null,
    { immediate: false, resetOnExecute: false },
  );

  // ===========================================================================
  // READ - Fetch Post Items (with caching)
  // FIXED: Accept delay as first parameter
  // ===========================================================================
  const {
    isLoading: isFetchingPostItems,
    error: fetchPostItemsError,
    execute: fetchPostItems,
  } = useAsyncState(
    async (postId, force = false) => {
      const postIdInt = parseInt(postId);

      if (!force && fetchedPostIds.value.has(postIdInt)) {
        return orderBoxItemRepo.value.where('postId', postIdInt).get();
      }

      const items = await OrderBoxRepository.fetchPostItems(postIdInt);
      fetchedPostIds.value.add(postIdInt);
      return items;
    },
    null,
    { immediate: false, resetOnExecute: false },
  );

  const {
    isLoading: _isFetchingPostData,
    error: _fetchPostDataError,
    execute: fetchPostData,
  } = useAsyncState(
    async (postId, force = false) => {
      const postIdInt = parseInt(postId);

      if (!force && fetchedPostIds.value.has(postIdInt)) {
        return orderBoxItemRepo.value.where('postId', postIdInt).get();
      }

      const params = {
        include: 'mediables,media,channel,products,categories,products.variants',
        withCount: 'bookmarks',
      };

      const response = await useAxiosRepo(Post).api().get(`/posts/${postIdInt}`, {
        params,
        save: false,
      });

      if (response?.response?.data?.data) {
        const fresh = response.response.data.data;
        useRepo(Post).save(fresh);
        // The card she saved from may be reading the bookmarks or the تدبيسة
        // table instead (models/posts/postFeedModels.js) — refresh the copies
        // they already hold, so its tag and «محجوزة ليك» line catch up there
        // too. Never an insert: those tables list only what their feed loaded.
        patchHeldPost(fresh, [TadbeesaPost, Bookmark]);

        await nextTick(); // Allow save to complete before proceeding
        const postData =  useRepo(Post).with('channel').with('products').with('media').find(postIdInt);

        fetchedPostIds.value.add(postIdInt);
        return postData;

      }

    },
    null,
    { immediate: false, resetOnExecute: false },
  );

  // ===========================================================================
  // UPDATE - Box Details
  // FIXED: Accept delay as first parameter
  // ===========================================================================
  const {
    isLoading: isUpdatingBox,
    error: updateBoxError,
    execute: updateBox,
  } = useAsyncState(
    async (boxId, updates) => {
      const updatedBox = await OrderBoxRepository.update(boxId, updates);
      fetchedBoxIds.value.delete(boxId);
      return updatedBox;
    },
    null,
    { immediate: false, resetOnExecute: false },
  );

  // ===========================================================================
  // UPDATE - Request Shipping
  // FIXED: Accept delay as first parameter
  //
  // `cancelOutOfStock` is the ids of the out-of-stock lines she approved
  // cancelling in the confirm (ship-ready pack S-K) — usage:
  // `requestShipping(0, boxId, ['12', '15'])`. Omitted / empty = no approval.
  // ===========================================================================
  const {
    isLoading: isRequestingShipping,
    error: requestShippingError,
    execute: requestShipping,
  } = useAsyncState(
    async (boxId, cancelOutOfStock = []) => {
      const updatedBox = await OrderBoxRepository.requestShipping(boxId, cancelOutOfStock);
      fetchedBoxIds.value.delete(boxId);
      return updatedBox;
    },
    null,
    { immediate: false, resetOnExecute: false },
  );

  // ===========================================================================
  // UPDATE - Re-open a carton she already asked to ship
  //
  // READY → ACTIVE, hers to do while the packaging team has not sealed it.
  // Same shape as requestShipping above, and it evicts the same cache entry:
  // the reversal changes the LINE statuses too, so a stale box here would show
  // her rows that still claim to be packaged.
  // ===========================================================================
  const {
    isLoading: isReopeningBox,
    error: reopenBoxError,
    execute: reopenBox,
  } = useAsyncState(
    async (boxId) => {
      const updatedBox = await OrderBoxRepository.reopen(boxId);
      fetchedBoxIds.value.delete(boxId);
      return updatedBox;
    },
    null,
    { immediate: false, resetOnExecute: false },
  );

  // ===========================================================================
  // UPDATE - «خلصت طلباتي», open ordering again, and the review complaint
  //
  // The done-ordering pack (brief 2026-10-17-done-ordering-review). All three
  // write the carton and evict its cache entry like requestShipping does, so the
  // next read is the server's view.
  // ===========================================================================
  const {
    isLoading: isMarkingDoneOrdering,
    error: doneOrderingError,
    execute: doneOrdering,
  } = useAsyncState(
    async (boxId) => {
      const updatedBox = await OrderBoxRepository.doneOrdering(boxId);
      fetchedBoxIds.value.delete(boxId);
      return updatedBox;
    },
    null,
    { immediate: false, resetOnExecute: false },
  );

  const {
    isLoading: isResumingOrdering,
    error: resumeOrderingError,
    execute: resumeOrdering,
  } = useAsyncState(
    async (boxId) => {
      const updatedBox = await OrderBoxRepository.resumeOrdering(boxId);
      fetchedBoxIds.value.delete(boxId);
      return updatedBox;
    },
    null,
    { immediate: false, resetOnExecute: false },
  );

  const {
    isLoading: isSendingReviewComplaint,
    error: reviewComplaintError,
    execute: reviewComplaint,
  } = useAsyncState(
    async (boxId, text) => {
      const updatedBox = await OrderBoxRepository.reviewComplaint(boxId, text);
      fetchedBoxIds.value.delete(boxId);
      return updatedBox;
    },
    null,
    { immediate: false, resetOnExecute: false },
  );

  // ===========================================================================
  // ITEM OPERATIONS - Add Items
  // FIXED: Accept delay as first parameter
  // ===========================================================================
  const {
    isLoading: isAddingItems,
    error: addItemsError,
    execute: addItems,
  } = useAsyncState(
    async (boxId, productId, items) => {
      if (!guard('addToCart')) return { success: false, blocked: true };
      if (!boxId || !items?.length) {
        throw new Error('Box ID and items are required');
      }

      const updatedBox = await OrderBoxRepository.addItems(boxId, productId, items);
      fetchedBoxIds.value.delete(boxId);

      return { success: true, box: updatedBox, itemsCount: items.length };
    },
    null,
    { immediate: false, resetOnExecute: false },
  );

  // ===========================================================================
  // ITEM OPERATIONS - Update Item
  // FIXED: Accept delay as first parameter
  //
  // Cancellation no longer flows through here — callers must use cancelItem /
  // uncancelItem below, which hit the dedicated JSON:API actions. Stripping
  // `status` from any other update keeps that contract honest.
  // ===========================================================================
  const {
    isLoading: isUpdatingItem,
    error: updateItemError,
    execute: updateItem,
  } = useAsyncState(
    async (boxId, itemId, updates) => {
      const cleanUpdates = { ...updates };
      delete cleanUpdates.status;
      const updatedItem = await OrderBoxRepository.updateItem(boxId, itemId, cleanUpdates);
      fetchedBoxIds.value.delete(boxId);
      return updatedItem;
    },
    null,
    { immediate: false, resetOnExecute: false },
  );

  // ===========================================================================
  // ITEM OPERATIONS - Cancel Item (dedicated JSON:API action)
  // POST /api/v1/order-box-items/{id}/cancel
  //
  // throwError:true is required so the typed error (with .code set to
  // ORDER_BOX_ITEM_PLAN_LOCKED / ORDER_BOX_ITEM_STALE_STATE) propagates to
  // the caller's try/catch instead of being swallowed into error.value.
  // Without it 403/422 responses would silently disappear from the UI.
  // ===========================================================================
  const {
    isLoading: isCancellingItem,
    error: cancelItemError,
    execute: cancelItem,
  } = useAsyncState(
    async (boxId, itemId, reason) => {
      const updatedItem = await OrderBoxRepository.cancelItem(boxId, itemId, reason);
      fetchedBoxIds.value.delete(boxId);
      return updatedItem;
    },
    null,
    { immediate: false, resetOnExecute: false, throwError: true },
  );

  // ===========================================================================
  // ITEM OPERATIONS - Uncancel Item (dedicated JSON:API action)
  // POST /api/v1/order-box-items/{id}/uncancel
  // ===========================================================================
  const {
    isLoading: isUncancellingItem,
    error: uncancelItemError,
    execute: uncancelItem,
  } = useAsyncState(
    async (boxId, itemId, reason) => {
      const updatedItem = await OrderBoxRepository.uncancelItem(boxId, itemId, reason);
      fetchedBoxIds.value.delete(boxId);
      return updatedItem;
    },
    null,
    { immediate: false, resetOnExecute: false, throwError: true },
  );

  // ===========================================================================
  // ITEM OPERATIONS - Remove Item
  // FIXED: Accept delay as first parameter
  // ===========================================================================
  const {
    isLoading: isRemovingItem,
    error: removeItemError,
    execute: removeItem,
  } = useAsyncState(
    async (boxId, itemId) => {
      const updatedBox = await OrderBoxRepository.removeItem(boxId, itemId);
      fetchedBoxIds.value.delete(boxId);
      return updatedBox;
    },
    null,
    { immediate: false, resetOnExecute: false },
  );

  // ===========================================================================
  // DELETE - Close Box
  // FIXED: Accept delay as first parameter
  // ===========================================================================
  const {
    isLoading: isClosingBox,
    error: closeBoxError,
    execute: closeBox,
  } = useAsyncState(
    async (boxId) => {
      const closedBox = await OrderBoxRepository.close(boxId);
      fetchedBoxIds.value.delete(boxId);
      return closedBox;
    },
    null,
    { immediate: false, resetOnExecute: false },
  );

  // ===========================================================================
  // DELETE - Destroy Box (Permanent)
  // FIXED: Accept delay as first parameter
  // ===========================================================================
  const {
    isLoading: isDeletingBox,
    error: deleteBoxError,
    execute: deleteBox,
  } = useAsyncState(
    async (boxId) => {
      await OrderBoxRepository.destroy(boxId);
      fetchedBoxIds.value.delete(boxId);
      if (selectedBoxId.value === boxId) {
        selectedBoxId.value = null;
      }
      return true;
    },
    null,
    { immediate: false, resetOnExecute: false },
  );

  // ===========================================================================
  // UTILITIES
  // ===========================================================================

  /**
   * Get initial form data for product variants
   * Returns structured data matching existing cart items
   */
  const getInitialFormData = (boxId, productId, variants) => {
    if (!boxId || !variants?.length) {
      return {};
    }

    const formData = {};
    const boxIdInt = parseInt(boxId);
    const productIdInt = parseInt(productId);

    variants.forEach((variant) => {
      const storedItem = orderBoxItemRepo.value
        .where('orderBoxId', boxIdInt)
        .where('variantId', parseInt(variant.id))
        .where('productId', productIdInt)
        .first();

      formData[variant.id] = {
        qty: storedItem?.qty || 0,
        notes: storedItem?.notes || '',
        variant,
      };
    });

    return formData;
  };

  /**
   * Reset all cart state
   */
  const resetCart = () => {
    selectedBoxId.value = null;
  };

  /**
   * Clear fetch caches
   * Useful for forcing fresh data on next fetch
   */
  const clearCache = () => {
    fetchedBoxIds.value.clear();
    fetchedPostIds.value.clear();
  };

  // ===========================================================================
  // WATCHERS - Auto-fetch selected box when changed
  // ===========================================================================
  watch(selectedBoxId, async (newBoxId) => {
    if (newBoxId && !fetchedBoxIds.value.has(newBoxId)) {
      await fetchBox(0, newBoxId);
    }
  });

  // ===========================================================================
  // RETURN API
  // ===========================================================================
  return {
    // State
    selectedBoxId,

    // Computed
    orderBoxes,
    activeBoxes,
    closedBoxes,
    selectedBox,
    availableBoxes,

    // Create Operations
    createBox,
    isCreatingBox,
    createBoxError,

    // Read Operations
    fetchAllBoxes,
    isFetchingAll,
    fetchAllError,

    fetchBox,
    isFetchingBox,
    fetchBoxError,

    fetchPostItems,
    fetchPostData,
    isFetchingPostItems,
    fetchPostItemsError,

    // Update Operations
    updateBox,
    isUpdatingBox,
    updateBoxError,

    requestShipping,
    isRequestingShipping,
    requestShippingError,

    reopenBox,
    isReopeningBox,
    reopenBoxError,

    doneOrdering,
    isMarkingDoneOrdering,
    doneOrderingError,

    resumeOrdering,
    isResumingOrdering,
    resumeOrderingError,

    reviewComplaint,
    isSendingReviewComplaint,
    reviewComplaintError,

    // Item Operations
    addItems,
    isAddingItems,
    addItemsError,

    updateItem,
    isUpdatingItem,
    updateItemError,

    cancelItem,
    isCancellingItem,
    cancelItemError,

    uncancelItem,
    isUncancellingItem,
    uncancelItemError,

    removeItem,
    isRemovingItem,
    removeItemError,

    // Delete Operations
    closeBox,
    isClosingBox,
    closeBoxError,

    deleteBox,
    isDeletingBox,
    deleteBoxError,

    // Utilities
    getInitialFormData,
    resetCart,
    clearCache,
  };
}
